import Axios, { InternalAxiosRequestConfig } from 'axios';
import { SerializedError } from '@reduxjs/toolkit';
import accountContext from '../account/accountContext';
import { handle403Error } from './errorHandler';

const addAuthentication = (config: InternalAxiosRequestConfig) => {
  const token = accountContext.accessToken;
  if (token) {
    config.headers.authorization = `Bearer ${token}`;
    return;
  }
};

const requestInterceptor = (config: InternalAxiosRequestConfig) => {
  addAuthentication(config);
  config.headers.Accept = 'application/json';
  config.headers['X-UI-Language'] = localStorage.getItem('i18nextLng') || 'sk';

  return config;
};

// LOGOUT-AMPLIFIER fix (2026-04-30): bot-proxy endpoints can return 401 for
// reasons unrelated to the user's MachineID session (bot-side JWT signature
// mismatch, route inconsistency, bot auth misconfig). Treating those as
// session-expiration would log the user out of the whole app — see the
// `feedback_report_bot_bugs_not_mask.md` rationale. We carve those URLs
// out here so the error still propagates to the chat caller (chatBotSlice
// already surfaces it as chat-error UX), but the global logout/redirect
// is skipped. Real auth-endpoint 401s (/v1/auth/..., /v1/account/...)
// continue to logout as before.
const isBotProxyUrl = (url: string | undefined): boolean => {
  if (!url) return false;
  // Match both relative (`/v1/bot/...`) and absolute (`https://host/v1/bot/...`)
  // request URLs — axios may pass either depending on whether baseURL was
  // applied at config time.
  return /(^|\/)v1\/bot\//.test(url);
};

const responseErrorInterceptor = (error: any) => {
  const isBotProxy = isBotProxyUrl(error?.config?.url);

  if (error?.response?.status === 401) {
    if (!isBotProxy) {
      accountContext.logout();
      window.location.href = "/";
    }
  }

  // Bot-proxy 403s are NOT global permission denials — they come from the
  // bot's own auth pipeline (whitelist, bot JWT, etc.) and surfacing the
  // "Contact your administrator" toast for them is the wrong UX. The chat /
  // feedback caller handles the error inline.
  if (error?.response?.status === 403 && !isBotProxy) {
    handle403Error(error?.response?.data);
  }

  // Bot-proxy carve-out: preserve the original AxiosError so consumers can
  // read `error.response.status` (needed for 409 duplicate_feedback soft
  // success on the feedback endpoint, and similar status-driven branching).
  // The default SerializedError transform below drops `response`, which made
  // the v3 feedback slice see `status: 0` for every 4xx. (Bug fix 2026-05-23.)
  // Note: all bot-proxy axios callers (feedback, history, threads, context)
  // now receive AxiosError instead of SerializedError. `error.message` /
  // `error.code` are still readable on AxiosError; the only behavioral
  // difference is the message text comes from axios ("Request failed with
  // status code 500") rather than the server's `data.message` field. Callers
  // that want the server message can read `error.response?.data?.message`.
  if (isBotProxy) {
    return Promise.reject(error);
  }

  const reduxError: SerializedError = {
    message: error?.response?.data?.message,
    code: error?.response?.data?.errorCode || error?.response?.data?.code?.toString(),
  };

  return Promise.reject(reduxError);
};

const api = Axios.create({
  baseURL: (window as any).app.config.apiBaseUrl
});

api.interceptors.request.use(requestInterceptor);
api.interceptors.response.use(response => response, responseErrorInterceptor);

export default api;